import { Link } from "react-router-dom";
import { Scale, ShieldCheck, FileText, Gavel, ArrowRight, Eye, Activity, Check } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Top Bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <Scale className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.75} />
            <span className="font-serif text-xl font-medium tracking-tight">FOIA Assist</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">Platform</a>
            <a href="#process" className="hover:text-slate-900">Process</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="text-sm text-slate-700 hover:text-slate-900 hidden sm:inline-block" data-testid="nav-login">Sign in</Link>
            <Link to="/register" className="fa-btn-primary text-sm sm:text-base whitespace-nowrap" data-testid="nav-start-trial">
              <span className="hidden sm:inline">Start 45-day trial</span>
              <span className="sm:hidden">Start trial</span>
              <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section
        className="relative border-b border-slate-200"
        style={{
          backgroundImage: "url(/hero-courthouse.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="hero-overlay">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7 text-white">
              <div className="inline-flex items-center gap-2 border border-white/30 px-3 py-1 text-xs uppercase tracking-widest font-medium mb-6 sm:mb-8">
                <ShieldCheck className="w-3 h-3" strokeWidth={2} />
                For public agencies &amp; FOIA-obligated contractors
              </div>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-medium mb-6 sm:mb-8">
                Defensible FOIA responses.
                <br />
                <span className="text-[#C5A059]">In hours, not days or months.</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed mb-8 sm:mb-10">
                AI-assisted redaction with human-in-the-loop review, immutable audit trails,
                and a real-time risk dashboard. Purpose-built for public agencies and the
                private contractors who inherit FOIA obligations when they work for them.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/register" className="fa-btn-primary !bg-white !text-[#0B1B3D] !border-white hover:!bg-[#F8F9FA]" data-testid="hero-cta-trial">
                  Start 45-day free trial <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
                <Link to="/risk-assessment" className="text-white underline decoration-[#C5A059] decoration-2 underline-offset-4 text-sm">
                  Take the FOIA Risk Assessment →
                </Link>
              </div>
              <div className="mt-10 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-8 max-w-2xl border-t border-white/15 pt-8">
                <Metric value="Audit-ready" label="Immutable trail per case" />
                <Metric value="AI-assisted" label="Redaction w/ human sign-off" />
                <Metric value="45 days" label="Free trial · no card" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-b border-slate-200" id="features">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">The status quo</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium mb-6">
              FOIA backlogs are<br />a legal liability.
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Most agencies still process public records requests with email threads, shared
              drives, and manual highlighter passes. Missed deadlines and inconsistent
              redactions create costly litigation exposure and erode public trust.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: FileText,
                title: "AI Redaction with Exemption Tagging",
                body: "Claude-powered NER + classification flags Exemption 4/5/6/7 content and STATE PII with confidence scores. Every flag tagged by statute.",
              },
              {
                icon: Eye,
                title: "Human-in-the-Loop Review",
                body: "Officers approve, reject, or modify every AI flag inline. Nothing gets released without human sign-off.",
              },
              {
                icon: Gavel,
                title: "Court-Ready Audit Trail",
                body: "Immutable, timestamped, actor-attributed log of every action. One-click PDF export filed straight into discovery.",
              },
              {
                icon: Activity,
                title: "Real-Time Risk Dashboard",
                body: "Agency risk score 0-100 with breakdown by deadline compliance, redaction accuracy, backlog, audit completeness, and staffing.",
              },
            ].map((f) => (
              <div key={f.title} className="fa-card p-6">
                <f.icon className="w-5 h-5 text-[#0B1B3D] mb-4" strokeWidth={1.75} />
                <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-slate-200 bg-white" id="process">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            <div className="lg:col-span-4">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">How it works</div>
              <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium">From request to release — one workflow.</h2>
            </div>
            <div className="lg:col-span-8 space-y-0 divider-y border border-slate-200">
              {[
                ["01", "Request received", "Submitted by citizen via portal or entered manually. Case auto-created with statutory deadline."],
                ["02", "Documents uploaded", "Drag-and-drop PDFs, DOCX, or text. Per-document versioning."],
                ["03", "AI Scan", "Claude flags sensitive spans across exemption categories with rationale and confidence."],
                ["04", "Officer review", "Approve, reject, or modify each flag inline. All actions logged."],
                ["05", "Legal summary", "AI generates a redaction rationale summary, reviewed by counsel."],
                ["06", "Respond & archive", "Send redacted response. Court-ready audit PDF preserved."],
              ].map(([n, title, body]) => (
                <div key={n} className="grid grid-cols-12 gap-4 sm:gap-6 p-5 sm:p-6">
                  <div className="col-span-3 sm:col-span-2 font-mono text-xl sm:text-2xl text-[#C5A059]">{n}</div>
                  <div className="col-span-9 sm:col-span-10">
                    <div className="font-semibold mb-1">{title}</div>
                    <div className="text-sm text-slate-600">{body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING — single Standard plan */}
      <section id="pricing" className="border-b border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Pricing</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium mb-3">One plan. Everything included.</h2>
          <p className="text-slate-600 max-w-2xl mb-10">
            Straightforward annual pricing designed for a records officer&apos;s reality — full platform, 500 cases per year, three users. Extra cases billed by invoice.
          </p>

          <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-12 border border-slate-200 bg-white" data-testid="landing-pricing-card">
            <div className="md:col-span-5 p-8 sm:p-10 bg-[#0B1B3D] text-white">
              <div className="text-xs uppercase tracking-widest text-[#C5A059] mb-3">Standard</div>
              <div className="font-serif text-5xl sm:text-6xl font-medium leading-none mb-2">$3,999</div>
              <div className="text-sm text-white/70 mb-6">per agency / year</div>
              <div className="text-xs text-white/60 border-t border-white/10 pt-4">
                Includes 500 cases per year. Additional cases billed at <span className="text-[#C5A059] font-medium">$199 each</span> via manual invoice.
              </div>
            </div>
            <div className="md:col-span-7 p-8 sm:p-10 flex flex-col justify-center">
              <ul className="space-y-3 text-sm text-slate-700 mb-8">
                {[
                  "Up to 500 cases per year",
                  "$199 per additional case (invoiced manually)",
                  "3 users included",
                  "AI-assisted redaction with human sign-off",
                  "Court-ready audit trail PDF export",
                  "Public request submission portal",
                  "45-day free trial · no credit card",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#0B1B3D] mt-0.5 flex-shrink-0" strokeWidth={2} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="fa-btn-primary justify-center" data-testid="landing-pricing-trial">
                  Start 45-day trial <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
                <Link to="/billing" className="fa-btn-secondary justify-center" data-testid="landing-pricing-po">
                  Request PO / Invoice
                </Link>
              </div>
            </div>
          </div>

          <p className="mt-6 text-xs text-slate-500 max-w-3xl mx-auto text-center">
            Pay by credit card (Stripe) or by purchase order (NET-30). Prices in USD.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B1B3D] text-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight font-medium mb-4">Start your 45-day trial. No credit card.</h2>
            <p className="text-white/70 leading-relaxed max-w-2xl">
              No credit card. No procurement gymnastics. Full feature access, sample case pre-loaded,
              team onboarded in 15 minutes.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/register" className="fa-btn-primary !bg-white !text-[#0B1B3D] !border-white" data-testid="footer-cta-trial">
              Activate trial <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#0B1B3D] border-t border-white/10 text-white/60 text-xs">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="font-mono uppercase tracking-widest">FOIA Assist © {new Date().getFullYear()}</div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/terms" className="hover:text-white underline-offset-2 hover:underline decoration-[#C5A059]" data-testid="landing-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-white underline-offset-2 hover:underline decoration-[#C5A059]" data-testid="landing-footer-privacy">Privacy</Link>
            <Link to="/risk-assessment" className="hover:text-white underline-offset-2 hover:underline decoration-[#C5A059]" data-testid="landing-footer-risk">Risk Assessment</Link>
            <span>Designed with security best practices for FOIA-obligated organizations</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Metric({ value, label }) {
  return (
    <div>
      <div className="font-serif text-3xl font-medium text-white">{value}</div>
      <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{label}</div>
    </div>
  );
}
